/* iter-48 — Three-Act Synopsis Import dialog.
 *
 * 2-step flow:
 *   1. Drop a .txt / .md / .docx / .epub file (2000-4000 words validated server-side).
 *   2. Choose whether Claude should parse the synopsis into structured
 *      characters / worldbuilding / mood / story arc.
 *
 * On success, the active project's `story_bible` is populated and (if Claude
 * was used) extracted characters land in the Character Bible panel.
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, ScrollText, Sparkles, Check, Loader2, FileText, AlertCircle, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImportThreeActSynopsisDialog({ open, onClose }) {
  const { project, loadProject } = usePW();
  const [step, setStep]       = useState(1);
  const [file, setFile]       = useState(null);
  const [useClaude, setClaude] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setStep(1); setFile(null); setClaude(true); setResult(null); setBusy(false); };
  const closeAndReset = () => { reset(); onClose?.(); };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file || !project) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('use_claude', useClaude ? 'true' : 'false');
      const { data } = await axios.post(
        `${API}/projects/${project.id}/import/three-act-synopsis`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000, withCredentials: true },
      );
      setResult(data);
      setStep(3);
      // Pull fresh project + characters so the panels update.
      try { await loadProject?.(project.id, { silent: true }); } catch { /* ignore */ }
      if (useClaude) {
        const chars  = data.characters_imported  || 0;
        const rels   = data.relationships_mapped || 0;
        const relMsg = rels > 0 ? ` · ${rels} relationship${rels === 1 ? '' : 's'} mapped` : '';
        toast.success(
          `All bibles populated · ${chars} character${chars === 1 ? '' : 's'} added${relMsg}`,
          { description: 'Story Bible · World Builder · Plot Extraction · Character cards · Relationship Map' },
        );
      } else {
        toast.success('Synopsis stored — fill in characters & variables manually');
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 && typeof detail === 'string' && detail.startsWith('Synopsis must be')) {
        toast.error(detail);
      } else if (status === 402) {
        toast.error('Locked in demo mode — sign up free to import a synopsis.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Import failed.');
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndReset(); }}>
      <DialogContent className="max-w-xl" data-testid="import-3act-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-primary" /> Import Synopsis to Story Bible
          </DialogTitle>
          <DialogDescription>
            Drop a 2,000–4,000 word three-part synopsis (Act 1 / Act 2 / Act 3).
            Optional Claude pass will populate the Character Bible, mood, themes and worldbuilding.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 — file drop */}
        {step === 1 && (
          <div className="space-y-3">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border p-8 text-center hover:border-primary/60 hover:bg-secondary/20"
              data-testid="import-3act-dropzone"
            >
              {file
                ? <FileText className="h-7 w-7 text-primary" />
                : <Upload className="h-7 w-7 text-primary" />}
              <div className="text-[13px] font-semibold">
                {file ? file.name : 'Drop your synopsis here'}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB · click to replace`
                  : '.txt / .md / .docx / .epub · 2,000–4,000 words · or click to browse'}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".txt,.md,.markdown,.docx,.epub"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="import-3act-input"
              />
            </div>

            <div className="rounded-md border border-border/60 bg-secondary/20 p-3 text-[11.5px] text-muted-foreground">
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-foreground">
                <AlertCircle className="h-3 w-3" /> Tip
              </div>
              Label each act clearly (<code className="rounded bg-background px-1">## Act I</code>,
              <code className="ml-1 rounded bg-background px-1">## Act II</code>,
              <code className="ml-1 rounded bg-background px-1">## Act III</code>) so the importer
              splits cleanly. No headings? It&apos;ll fall back to thirds.
            </div>

            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={closeAndReset} data-testid="import-3act-cancel">Cancel</Button>
              <Button size="sm" disabled={!file} onClick={() => setStep(2)} className="gap-1.5" data-testid="import-3act-next">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — Claude pass? */}
        {step === 2 && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setClaude(true)}
              data-testid="import-3act-use-claude"
              className={`flex w-full items-start gap-3 rounded-md border p-3 text-left transition ${useClaude ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
            >
              <Sparkles className={`mt-0.5 h-4 w-4 shrink-0 ${useClaude ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[13px] font-semibold">
                  Let Claude parse the synopsis (recommended)
                  {useClaude && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted-foreground leading-relaxed">
                  One LLM call populates ALL bibles: Story Bible (arc, mood, themes), World Builder
                  (setting, era, magic/tech), Plot Extraction (3-act structure, protagonist goal/need),
                  Character cards with full 3-beat arcs &amp; motivations, and a Relationship Map.
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setClaude(false)}
              data-testid="import-3act-skip-claude"
              className={`flex w-full items-start gap-3 rounded-md border p-3 text-left transition ${!useClaude ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
            >
              <FileText className={`mt-0.5 h-4 w-4 shrink-0 ${!useClaude ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[13px] font-semibold">
                  Just store the raw synopsis
                  {!useClaude && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted-foreground leading-relaxed">
                  Saves the synopsis &amp; act splits into the project. You&apos;ll fill in characters and
                  variables manually. No LLM cost.
                </div>
              </div>
            </button>

            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="import-3act-back">Back</Button>
              <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5" data-testid="import-3act-submit">
                {busy
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing…</>
                  : <>{useClaude ? 'Parse with Claude' : 'Save synopsis'} <ArrowRight className="h-3.5 w-3.5" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — result */}
        {step === 3 && result && (
          <div className="space-y-3">
            <div className="rounded-md border border-success/40 bg-success/5 p-3">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-success">
                <Check className="h-4 w-4" /> All bibles populated
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
                <div>Word count: <span className="text-foreground">{result.word_count}</span></div>
                <div>Characters: <span className="text-foreground">{result.characters_imported}</span></div>
                <div>Relationships: <span className="text-foreground">{result.relationships_mapped ?? '—'}</span></div>
                <div>Story Bible: <span className="text-foreground">{result.claude_parsed ? 'populated' : 'raw only'}</span></div>
              </div>
              {result.claude_parsed && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {['Story Bible','World Builder','Plot Extraction','Character Bible','Relationship Map'].map((panel) => (
                    <span key={panel} className="rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success">
                      {panel}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {result.story_bible?.mood && (
              <div className="text-[12px]"><b>Mood:</b> <span className="text-muted-foreground">{result.story_bible.mood}</span></div>
            )}
            {Array.isArray(result.story_bible?.themes) && result.story_bible.themes.length > 0 && (
              <div className="text-[12px]"><b>Themes:</b> <span className="text-muted-foreground">{result.story_bible.themes.join(', ')}</span></div>
            )}
            {result.story_bible?.story_arc && (
              <div className="text-[12px] leading-snug"><b>Story arc:</b> <span className="text-muted-foreground">{result.story_bible.story_arc}</span></div>
            )}
            <div className="flex items-center justify-end">
              <Button size="sm" onClick={closeAndReset} data-testid="import-3act-done">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
